import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { colorTextMuted } from "src/design/tokens";
import { AccountRequest } from "src/data/admin/queries/accountRequest";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";

interface ApproveRequestDialogProps {
  request: AccountRequest | null;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (restaurantId: string) => void;
}

export const ApproveRequestDialog = ({
  request,
  isSubmitting,
  onClose,
  onConfirm,
}: ApproveRequestDialogProps) => {
  const { restaurants, isLoading: restaurantsLoading } =
    useContext(RestaurantContext);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState("");

  // Reset the selection whenever the dialog opens for a new request.
  useEffect(() => {
    if (request) setSelectedRestaurantId("");
  }, [request]);

  // Only restaurants not yet attached to an organization can be assigned to a
  // newly approved request.
  const unassignedRestaurants = useMemo(
    () => restaurants.filter((r) => r.organizationId === null),
    [restaurants],
  );

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <Dialog open={!!request} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Approve Request</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <DialogContentText>
            Are you sure you want to approve the request from{" "}
            <strong>
              {request?.firstName} {request?.lastName}
            </strong>
            ? This will create an organization and send them their credentials
            by email.
          </DialogContentText>
          <FormControl
            size="small"
            fullWidth
            disabled={restaurantsLoading || unassignedRestaurants.length === 0}
          >
            <InputLabel>Restaurant</InputLabel>
            <Select
              value={selectedRestaurantId}
              label="Restaurant"
              onChange={(e) => setSelectedRestaurantId(e.target.value)}
            >
              {unassignedRestaurants.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                  <Typography
                    variant="caption"
                    sx={{ ml: 1, color: colorTextMuted }}
                  >
                    — {r.city}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: colorTextMuted }}>
            {!restaurantsLoading && unassignedRestaurants.length === 0
              ? "No unassigned restaurants are available. Every restaurant already belongs to an organization."
              : "Only restaurants not yet attached to an organization are listed. The selected restaurant will be attached to the new organization."}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton color="inverted" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </CustomButton>
        <CustomLoadingButton
          loading={isSubmitting}
          onSubmit={() => onConfirm(selectedRestaurantId)}
          label="Approve"
          color="success"
          fullwidth={false}
          disabled={!selectedRestaurantId}
        />
      </DialogActions>
    </Dialog>
  );
};
